import Popper, { PopperPlacementType, PopperProps } from '@mui/material/Popper'
import Fade from '@mui/material/Fade'
import { disabledGrey } from '$constants'
import { Stack, IconButton, Button, Theme, useTheme } from '@mui/material'
import {
  LocalizationProvider,
  DateCalendar,
  PickersActionBarAction,
  TimeStepOptions,
} from '@mui/x-date-pickers-pro'
import dayjs, { Dayjs } from 'dayjs'
import CustomDateTimePickerActionBar, {
  isValueWithinRange,
} from '../CustomDateTimePickerActionBar'
import LoadedTextField from '../LoadedTextField'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { FC, memo, useEffect, useState } from 'react'

export type LoadedDateTimePickerPopperProps = {
  pickerValue: Dayjs | null
  prevPickerValue: Dayjs | null
  layout: 'time' | 'date' | 'datetime'
  maxDateTime?: Dayjs | null
  minDateTime?: Dayjs | null
  timeSteps?: TimeStepOptions
  popperPlacement?: PopperPlacementType
  actions?: PickersActionBarAction[]
  required?: boolean
  handleDateChange: (date: Dayjs | null) => void
  onAccept: (date: Dayjs | null) => void
  onClear: () => void
  onCancel: () => void
  onSetToday: (currentDate: Dayjs) => void
  close: () => void
} & PopperProps

const LoadedDateTimePickerPopper: FC<LoadedDateTimePickerPopperProps> = ({
  id,
  open,
  anchorEl,
  pickerValue,
  prevPickerValue,
  layout,
  maxDateTime,
  minDateTime,
  timeSteps,
  popperPlacement = 'top',
  actions,
  required,
  handleDateChange: handleDateTimePickerChange,
  onAccept,
  onCancel,
  onClear,
  onSetToday,
  close,
}) => {
  const theme = useTheme<Theme>()
  const [value, setValue] = useState<Dayjs | null>(
    pickerValue ? dayjs(pickerValue) : null
  )

  // Check if it's before noon (AM) or after (PM)
  const isValueAM: boolean = value?.format('A') === 'AM'

  const changeAmPm = (value: Dayjs | null) => {
    if (!value) return null

    const isAm = value?.format('A') === 'AM'
    // Convert from AM to PM
    if (isAm) {
      return value?.add(12, 'hour')
    } else {
      // Convert from PM to AM
      return value?.subtract(12, 'hour')
    }
  }

  const checkAmPmDisabled = () => {
    if (!minDateTime && !maxDateTime) return false

    const updatedValue = value ? changeAmPm(value) : null
    if (!minDateTime && maxDateTime)
      return updatedValue?.isAfter(maxDateTime) ?? false

    if (!maxDateTime && minDateTime)
      return updatedValue?.isBefore(minDateTime) ?? false

    return (
      updatedValue?.isBetween(minDateTime, maxDateTime, 'day', '[]') || false
    )
  }

  const isAmPmDisabled = checkAmPmDisabled()

  useEffect(() => {
    setValue(pickerValue)
  }, [pickerValue])

  if (!open) return

  const handleDateChange = (newDate) => {
    handleDateTimePickerChange(newDate)
    setValue(newDate)
  }

  /**
   * Function to keep the AM or PM from the original value a avoid changing the AM or PM
   * because of using 1-12 hour format when setting the time
   * @param newValue new value to reset the AM or PM depending on the original value
   * @returns Dayjs
   */
  const keepAmOrPm = (newValue: Dayjs) => {
    const isNewValueAM = newValue.format('A') === 'AM'
    if (isValueAM && !isNewValueAM) {
      // Convert from PM to AM
      newValue = newValue.subtract(12, 'hour')
    } else if (!isValueAM && isNewValueAM) {
      // Convert from AM to PM
      newValue = newValue.add(12, 'hour')
    }

    return newValue
  }

  // Helper that takes an adjustment function that returns a new datejs instance
  // with an adjustment made to it.
  const timeAdjuster = (
    adjustmentFn: () => Dayjs,
    isHourUpdate: boolean = false
  ) => {
    if (!value) return null

    let newValue = adjustmentFn()
      .set('year', value.year())
      .set('month', value.month())
      .set('date', value.date())

    if (isHourUpdate) newValue.set('hour', value.hour())

    const isNewValueAM = newValue.format('A') === 'AM'
    if (isValueAM && !isNewValueAM) {
      // Convert from PM to AM
      newValue = newValue.subtract(12, 'hour')
    } else if (!isValueAM && isNewValueAM) {
      // Convert from AM to PM
      newValue = newValue.add(12, 'hour')
    }

    newValue = keepAmOrPm(newValue)

    return newValue
  }

  const incrementHour = () =>
    timeAdjuster(() => {
      const hourTimeSteps = timeSteps?.hours ?? 1
      return value!.add(hourTimeSteps, 'hour')
    }, true)

  const decrementHour = () =>
    timeAdjuster(() => {
      const hourTimeSteps = timeSteps?.hours ?? 1
      return value!.subtract(hourTimeSteps, 'hour')
    }, true)

  const incrementMinute = () =>
    timeAdjuster(() => {
      const minutesTimeSteps = timeSteps?.minutes ?? 1
      return value!.add(minutesTimeSteps, 'minute')
    })

  const decrementMinute = () =>
    timeAdjuster(() => {
      const minutesTimeSteps = timeSteps?.minutes ?? 1
      return value!.subtract(minutesTimeSteps, 'minute')
    })

  const handleHourChange = (event) => {
    const insertedValue = event.target.value
    const parsedValue = parseInt(insertedValue, 10)
    if (isNaN(parsedValue) || parsedValue < 1 || parsedValue > 12) {
      return insertedValue.slice(0, -1)
    }

    return insertedValue
  }

  const handleHourBlur = (event) => {
    const insertedValue = event.target.value

    const newValue = value ? value.set('hour', +insertedValue) : null

    return setValueOnBlur(newValue)?.format('hh')
  }

  const setValueOnBlur = (insertedValue: Dayjs | null) => {
    if (
      insertedValue &&
      isValueWithinRange(insertedValue, minDateTime, maxDateTime)
    ) {
      insertedValue = keepAmOrPm(insertedValue)
      setValue(insertedValue)
      return insertedValue
    } else {
      setValue(null)
      return null
    }
  }

  const handleMinuteChange = (event) => {
    const insertedValue = event.target.value
    const parsedValue = parseInt(insertedValue, 10)
    if (isNaN(parsedValue) || parsedValue < 0 || parsedValue > 59) {
      return insertedValue.slice(0, -1)
    }

    return insertedValue
  }

  const handleMinuteBlur = (event) => {
    const insertedValue = event.target.value

    const newValue = value ? value.set('minute', insertedValue) : null

    return setValueOnBlur(newValue)?.format('mm')
  }

  const incrementedHour = incrementHour()
  const decrementedHour = decrementHour()
  const incrementedMinute = incrementMinute()
  const decrementedMinute = decrementMinute()

  const isIncrementHourDisabled: boolean = !isValueWithinRange(
    incrementedHour,
    minDateTime,
    maxDateTime
  )
  const isDecrementHourDisabled: boolean = !isValueWithinRange(
    decrementedHour,
    minDateTime,
    maxDateTime
  )
  const isIncrementMinuteDisabled: boolean = !isValueWithinRange(
    incrementedMinute,
    minDateTime,
    maxDateTime
  )
  const isDecrementMinuteDisabled: boolean = !isValueWithinRange(
    decrementedMinute,
    minDateTime,
    maxDateTime
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-nz'}>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        transition
        placement={popperPlacement}
        sx={{
          zIndex: 2000,
          backgroundColor: theme.palette.background.paper,
          boxShadow:
            '0px 5px 5px -3px rgba(0,0,0,0.1),0px 8px 10px 1px rgba(0,0,0,0.05),0px 3px 14px 2px rgba(0,0,0,0.06)',
          '.MuiPaper-root': {
            padding: '.5rem !important',
          },
        }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={10}>
            <Stack spacing={0}>
              <Stack direction="row" alignSelf={'center'}>
                {(layout === 'date' || layout === 'datetime') && (
                  <DateCalendar
                    value={value}
                    onChange={handleDateChange}
                    sx={{
                      height: '32.4rem',
                      width: '30rem',
                      paddingX: '1rem',
                      '& .MuiPickersDay-root.Mui-disabled': {
                        color: `${disabledGrey} !important`,
                      },
                      '.MuiDayCalendar-slideTransition': {
                        minHeight: '22.8rem !important',
                      },
                      '.MuiPickersCalendarHeader-root': {
                        marginBottom: 0,
                        marginTop: '1rem',
                        paddingLeft: '1rem',
                        paddingRight: '1rem',
                      },
                    }}
                    maxDate={maxDateTime}
                    minDate={minDateTime}
                  />
                )}

                {(layout === 'datetime' || layout === 'time') && (
                  <Stack
                    direction={'row'}
                    spacing={1}
                    paddingRight={'2rem'}
                    marginLeft={'0 !important'}
                    {...(layout === 'time' && {
                      paddingY: '1rem',
                      paddingX: '2rem',
                      justifyContent: 'center',
                      width: '100%',
                    })}
                  >
                    <Stack
                      direction="column"
                      alignItems={'center'}
                      alignSelf={'center'}
                      spacing={1}
                      marginLeft={'0 !important'}
                    >
                      <IconButton
                        size="small"
                        onClick={() => {
                          setValue(incrementedHour)
                          handleDateTimePickerChange(incrementedHour)
                        }}
                        disabled={isIncrementHourDisabled}
                        sx={{
                          padding: 0,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <KeyboardArrowUpIcon />
                      </IconButton>
                      <LoadedTextField
                        name="hour"
                        size="small"
                        value={value ? `${value?.format('hh')}` : ''}
                        onChange={handleHourChange}
                        onBlur={handleHourBlur}
                        inputProps={{
                          min: 1,
                          max: 12,
                          maxLength: 2,
                          inputMode: 'numeric',
                        }}
                        textFieldSxProps={{
                          textAlignLast: 'center',
                          '.MuiInputBase-root': {
                            width: '5rem',
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          setValue(decrementedHour)
                          handleDateTimePickerChange(decrementedHour)
                        }}
                        disabled={isDecrementHourDisabled}
                        sx={{
                          padding: 0,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Stack>
                    <Stack
                      alignItems={'center'}
                      alignSelf={'center'}
                      spacing={1}
                    >
                      <IconButton
                        size="small"
                        onClick={() => {
                          setValue(incrementedMinute)
                          handleDateTimePickerChange(incrementedMinute)
                        }}
                        disabled={isIncrementMinuteDisabled}
                        sx={{
                          padding: 0,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <KeyboardArrowUpIcon />
                      </IconButton>
                      <LoadedTextField
                        name="minute"
                        size="small"
                        value={value ? `${value?.format('mm')}` : ''}
                        onChange={handleMinuteChange}
                        onBlur={handleMinuteBlur}
                        inputProps={{
                          min: 0,
                          max: 59,
                          maxLength: 2,
                          inputMode: 'numeric',
                        }}
                        textFieldSxProps={{
                          textAlignLast: 'center',
                          '.MuiInputBase-root': {
                            width: '5rem',
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          setValue(decrementedMinute)
                          handleDateTimePickerChange(decrementedMinute)
                        }}
                        disabled={isDecrementMinuteDisabled}
                        sx={{
                          padding: 0,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Stack>
                    <Stack alignItems={'center'} alignSelf={'center'}>
                      <Button
                        variant="outlined"
                        disabled={isAmPmDisabled}
                        sx={{
                          height: '3.2rem',
                          padding: 0,
                          minWidth: '5rem',
                        }}
                        size="small"
                        onClick={() => setValue(changeAmPm(value))}
                      >
                        {isValueAM ? 'AM' : 'PM'}
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Stack>
              <CustomDateTimePickerActionBar
                onAccept={() => {
                  onAccept(value)
                  close()
                }}
                onClear={() => {
                  setValue(null)
                  onClear()
                }}
                onCancel={() => {
                  setValue(prevPickerValue)
                  onCancel()
                }}
                onSetToday={() => {
                  // Original date
                  const currentDate = dayjs() // Current date and time

                  // Extract year, month, and day from the original date
                  const year = currentDate.year()
                  const month = currentDate.month() // Month is zero-based (0 for January)
                  const day = currentDate.date()

                  // New date with modified year, month, and day (time remains unchanged)
                  const newDate =
                    value
                      ?.set('date', day)
                      .set('month', month)
                      .set('year', year) || dayjs().startOf('day')

                  setValue(newDate)
                  onSetToday(newDate)
                }}
                actions={actions}
                layout={layout}
                required={required}
                maxDateTime={maxDateTime}
                minDateTime={minDateTime}
                value={value}
              />
            </Stack>
          </Fade>
        )}
      </Popper>
    </LocalizationProvider>
  )
}

export default memo(LoadedDateTimePickerPopper)
